import { Html5Qrcode } from "html5-qrcode";
import { BarcodeScanner } from '@capacitor-mlkit/barcode-scanning';
import { useState, useMemo, useEffect, useRef } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BillingBar } from "@/components/BillingBar";
import { AddProductDialog } from "@/components/AddProductDialog";
import { ProductCardSkeleton } from "@/components/LoadingSkeleton";
import { EmptyState } from "@/components/EmptyState";
import { useToast } from "@/hooks/use-toast";
import { useData } from "@/contexts/DataContext";
import { useAuth } from "@/hooks/use-auth";
import { useAdTrigger } from "@/hooks/useShowAds";
import { Haptics } from '@capacitor/haptics';
import { BleClient } from '@capacitor-community/bluetooth-le';
import { Dialog as NativeDialog } from '@capacitor/dialog';
import {
  Plus,
  Search,
  Package,
  Scan,
  Heart,
  Zap,
  Barcode,
  ChevronDown,
  Keyboard,
  Trash2,
  Bell,
  TrendingUp,
  Sparkles,
  Pencil,
  Edit2,
  Minus,
  ShoppingCart,
  X,
  Printer,
  Bluetooth,
  Usb,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Flashlight,
  MoreVertical,
  ChevronRight
} from "lucide-react";
import BarcodeRenderer from "react-barcode";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import barcodeIcon from "@assets/barcode-icon_1771351246533.webp";
import { Logo } from "@/components/Logo";
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import type { Product } from "@/lib/googleSheetsService";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { RecordSuccessPopup } from "@/components/RecordSuccessPopup";
import {
  fetchProductDataZeroCost,
  saveToMasterCatalog,
} from "@/lib/googleSheetsService";
import { sendToBTPrinter, connectBTPrinter, getCachedBTPrinter } from "@/lib/btPrinter";
import { buildBarcodeEscPos } from "@/lib/barcodeUtils";
import { buildBarcodeTSPL, buildBarcodeZPL } from "@/lib/labelPrinterUtils";
import {
  connectUSBPrinter,
  sendToUSBPrinter,
  getCachedUSBPrinter,
} from "@/lib/usbPrinter";

interface SelectedItem {
  productId: string;
  productName: string;
  quantity: number;
  rate: number;
}

const BLE_PRINTER_SERVICES = [
  "0000ff00-0000-1000-8000-00805f9b34fb",
  "49535343-fe7d-4ae5-8fa9-9fafd205e455",
  "000018f0-0000-1000-8000-00805f9b34fb",
  "e7810a71-73ae-499d-8c15-faa9aef0c3f2",
  "0000fee7-0000-1000-8000-00805f9b34fb",
];
const BLE_WRITE_CHARACTERISTICS = [
  "0000ff02-0000-1000-8000-00805f9b34fb",
  "49535343-8841-43f4-a8d4-ecbe34729bb3",
  "00002af1-0000-1000-8000-00805f9b34fb",
  "0000fee9-0000-1000-8000-00805f9b34fb",
];

async function sendBarcodesToPrinter(
  characteristic: any,
  data: Uint8Array,
): Promise<void> {
  const chunkSize = 20;
  const useNoResponse = characteristic.properties.writeWithoutResponse;
  for (let i = 0; i < data.length; i += chunkSize) {
    const chunk = data.slice(i, i + chunkSize);
    if (useNoResponse) {
      await characteristic.writeValueWithoutResponse(chunk);
    } else {
      await characteristic.writeValue(chunk);
    }
    await new Promise((r) => setTimeout(r, 50));
  }
}

// NATIVE CAPACITOR BLUETOOTH WRITE LOGIC
async function writeToBleDevice(deviceId: string, data: Uint8Array) {
  const BLE_SERVICES = [
    "0000ff00-0000-1000-8000-00805f9b34fb",
    "49535343-fe7d-4ae5-8fa9-9fafd205e455",
    "000018f0-0000-1000-8000-00805f9b34fb",
    "e7810a71-73ae-499d-8c15-faa9aef0c3f2",
    "0000fee7-0000-1000-8000-00805f9b34fb",
  ];
  const services = await BleClient.getServices(deviceId);
  let targetService: string | null = null;
  let targetChar: string | null = null;

  for (const s of services) {
    if (BLE_SERVICES.includes(s.uuid.toLowerCase())) {
      for (const c of s.characteristics) {
        if (c.properties.write || c.properties.writeWithoutResponse) {
          targetService = s.uuid;
          targetChar = c.uuid;
          break;
        }
      }
    }
    if (targetChar) break;
  }

  if (!targetService || !targetChar) {
    throw new Error("Printer connected, but no writable channel found.");
  }

  const dataView = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const chunkSize = 100;
  for (let i = 0; i < dataView.byteLength; i += chunkSize) {
    const end = Math.min(i + chunkSize, dataView.byteLength);
    const chunk = new DataView(dataView.buffer, dataView.byteOffset + i, end - i);
    await BleClient.writeWithoutResponse(deviceId, targetService, targetChar, chunk);
    await new Promise(r => setTimeout(r, 40));
  }
}

export default function Products() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const {
    products,
    transactions,
    isLoading,
    createProduct,
    editProduct,
    removeProduct,
    createTransactions,
    createStockExpense,
    userMode,
    businessProfile,
    businessVerified,
    spreadsheetId,
    labelPrinterLanguage,
    labelWidth,      // <-- ADD THIS
    labelHeight,     // <-- ADD THIS
  } = useData();
  const triggerAd = useAdTrigger();

  const deleteProduct = removeProduct;

  const hasUnreadAlerts = useMemo(() => {
    if (userMode !== "pro") return false;
    try {
      const saved = localStorage.getItem("dh_read_notifications");
      const readIds = saved
        ? new Set<string>(JSON.parse(saved))
        : new Set<string>();
      return products.some((p) => {
        if (p.stock === undefined) return false;
        if (p.stock === 0) return !readIds.has(`oos-${p.id}-${p.stock}`);
        if (
          p.lowStockAlert &&
          p.lowStockAlert > 0 &&
          p.stock <= p.lowStockAlert
        )
          return !readIds.has(`low-${p.id}-${p.stock}`);
        return false;
      });
    } catch {
      return false;
    }
  }, [products, userMode]);

  const sortedProducts = useMemo(() => {
    if (!products) return [];

    const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const salesCount = new Map<string, number>();
    transactions.forEach((t) => {
      if (
        t.type === "sale" &&
        new Date(t.createdAt).getTime() >= thirtyDaysAgo
      ) {
        salesCount.set(
          t.productId,
          (salesCount.get(t.productId) || 0) + (t.quantity || 0),
        );
      }
    });

    return [...products].sort((a, b) => {
      const salesA = salesCount.get(a.id) || 0;
      const salesB = salesCount.get(b.id) || 0;
      if (salesB !== salesA) return salesB - salesA;
      return a.name.localeCompare(b.name);
    });
  }, [products, transactions]);

  const updateProduct = editProduct;

  const [pageSize, setPageSize] = useState(25); // <-- Added page size state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItems, setSelectedItems] = useState<Map<string, SelectedItem>>(new Map());
  const [billDiscount, setBillDiscount] = useState<number>(0);
  const [isGstBill, setIsGstBill] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [showChoiceDialog, setShowChoiceDialog] = useState(false);
  const [editProductData, setEditProductData] = useState<Product | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [isTorchOn, setIsTorchOn] = useState(false);
  const [showReviewPopup, setShowReviewPopup] = useState(false);
  const [scannerManualSearch, setScannerManualSearch] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchStatus, setSearchStatus] = useState("");
  const [dataSource, setDataSource] = useState<string | null>(null);
  const [showFavoriteOverlay, setShowFavoriteOverlay] = useState<{ show: boolean; isFavorite: boolean; }>({ show: false, isFavorite: false });
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [lastInvoiceData, setLastInvoiceData] = useState<{
    summary: string;
    total: number;
    items: any[];
    discount: number;
    orderId?: string;
    fullOrderId?: string;
    isGstBill?: boolean;
  } | null>(null);
  const [productToDeleteId, setProductToDeleteId] = useState<string | null>(null);
  const [duplicateProduct, setDuplicateProduct] = useState<Product | null>(null);
  const [isBillingMode, setIsBillingMode] = useState(false);
  const [notInStoreBarcode, setNotInStoreBarcode] = useState<string | null>(null);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [btBarcodeStatus, setBtBarcodeStatus] = useState<"idle" | "connecting" | "printing" | "success" | "error">("idle");
  const [btBarcodeError, setBtBarcodeError] = useState("");
  const [usbBarcodeStatus, setUsbBarcodeStatus] = useState<"idle" | "connecting" | "printing" | "success" | "error">("idle");
  const [usbBarcodeError, setUsbBarcodeError] = useState("");
  const [showCameraPermissionModal, setShowCameraPermissionModal] = useState(false);
  const [showPrinterChoice, setShowPrinterChoice] = useState(false);
  const [printFilter, setPrintFilter] = useState<"app" | "all">("app");
  const [printQty, setPrintQty] = useState(1);
  const [productQtys, setProductQtys] = useState<Record<string, number>>({});
  const [wasScanned, setWasScanned] = useState(false);

  // Action Sheet State
  const [actionProduct, setActionProduct] = useState<Product | null>(null);

  // Stock Manager State
  const [showStockManager, setShowStockManager] = useState(false);
  const [stockUpdates, setStockUpdates] = useState<Map<string, number>>(new Map());
  const [isSavingStock, setIsSavingStock] = useState(false);
  const [stockFilter, setStockFilter] = useState<"all" | "out" | "low">("all");

  const html5QrCodeRef = useRef<Html5Qrcode | null>(null);
  const lastScannedBarcodeRef = useRef<string>("");
  const lastScanTimeRef = useRef<number>(0);

  const atRiskProducts = useMemo(() => {
    return products.filter((p) => {
      const stock = (p as any).stock;
      if (stock === undefined || stock === null) return false;
      return stock <= ((p as any).lowStockAlert ?? 0);
    });
  }, [products]);

  const outOfStockProducts = useMemo(() => {
    return products.filter(
      (p) => (p as any).stock !== undefined && (p as any).stock <= 0,
    );
  }, [products]);

  const lowStockOnlyProducts = useMemo(() => {
    return products.filter((p) => {
      const stock = (p as any).stock;
      return stock !== undefined && stock > 0 && stock <= ((p as any).lowStockAlert ?? 0);
    });
  }, [products]);

  const openStockManager = () => {
    const initial = new Map<string, number>();
    atRiskProducts.forEach((p) => {
      initial.set(p.id, (p as any).stock ?? 0);
    });
    setStockUpdates(initial);
    setStockFilter("all");
    setShowStockManager(true);
  };

  const handleSaveStocks = async () => {
    setIsSavingStock(true);
    try {
      const updates = Array.from(stockUpdates.entries());
      for (const [id, newStock] of updates) {
        const product = products.find((p) => p.id === id);
        if (!product) continue;
        const originalStock = (product as any).stock ?? 0;
        if (stockFilter === "out" && !(originalStock <= 0)) continue;
        if (
          stockFilter === "low" &&
          !(originalStock >= 1 && originalStock <= ((product as any).lowStockAlert ?? 0))
        )
          continue;
        await updateProduct({ ...product, stock: newStock } as any);

        const stockAdded = newStock - originalStock;
        const costPrice = (product as any).costPrice || 0;
        if (userMode === "pro" && stockAdded > 0 && costPrice > 0) {
          try {
            await createStockExpense({
              productId: product.id,
              productName: product.name,
              quantity: stockAdded,
              rate: costPrice,
            });
          } catch (e) {
            console.error("Stock expense transaction failed:", e);
          }
        }

        await new Promise((resolve) => setTimeout(resolve, 400));
      }
      toast({ title: "Stock updated successfully" });
      setShowStockManager(false);
    } catch (error: any) {
      toast({
        title: "Failed to update stock",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSavingStock(false);
    }
  };

  const handleBulkFill = (value: number) => {
    const updated = new Map<string, number>();
    atRiskProducts.forEach((p) => {
      updated.set(p.id, value);
    });
    setStockUpdates(updated);
  };

  const generateInvoice = (
    saleItems: SelectedItem[],
    total: number,
    discount: number = 0,
  ) => {
    const estimatedHeight =
      45 + 20 + 10 + saleItems.length * 8 + 30 + 25 +
      (businessVerified ? 5 : 0); // badge advance — keep in sync with the DAILYHISAAB VERIFIED badge

    const doc = new jsPDF({
      unit: "mm",
      format: [80, estimatedHeight],
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    let yPos = 15;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    const bName = (
      businessProfile?.businessName || "RETAIL STORE"
    ).toUpperCase();
    doc.text(bName, pageWidth / 2, yPos, { align: "center" });
    yPos += 6;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);

    if (businessProfile?.businessAddress) {
      const addr = doc.splitTextToSize(
        businessProfile.businessAddress,
        pageWidth - 20,
      );
      doc.text(addr, pageWidth / 2, yPos, { align: "center" });
      yPos += addr.length * 4;
    }

    if (businessProfile?.businessPhone) {
      doc.text(`Tel: ${businessProfile.businessPhone}`, pageWidth / 2, yPos, {
        align: "center",
      });
      yPos += 4;
    }

    if (businessProfile?.businessEmail) {
      doc.text(`Email: ${businessProfile.businessEmail}`, pageWidth / 2, yPos, {
        align: "center",
      });
      yPos += 4;
    }

    if (businessProfile?.businessWebsite) {
      doc.text(`WEB: ${businessProfile.businessWebsite}`, pageWidth / 2, yPos, {
        align: "center",
      });
      yPos += 4;
    }

    yPos += 2;

    doc.setLineDashPattern([1, 1], 0);
    doc.line(5, yPos, pageWidth - 5, yPos);
    yPos += 6;

    doc.setFontSize(7);
    const dateStr = saleItems[0]?.createdAt
      ? new Date(saleItems[0].createdAt).toLocaleDateString()
      : new Date().toLocaleDateString();
    const timeStr = saleItems[0]?.createdAt
      ? new Date(saleItems[0].createdAt).toLocaleTimeString()
      : new Date().toLocaleTimeString();
    doc.text(`DATE: ${dateStr}`, 5, yPos);
    doc.text(`TIME: ${timeStr}`, pageWidth - 5, yPos, {
      align: "right",
    });
    yPos += 4;
    doc.text(`INV #: INV-${Date.now().toString().slice(-6)}`, 5, yPos);
    yPos += 6;

    doc.line(5, yPos, pageWidth - 5, yPos);
    yPos += 6;

    doc.setFont("helvetica", "bold");
    doc.text("Qty Item", 5, yPos);
    doc.text("Unit Price", 50, yPos, { align: "right" });
    doc.text("Total Price", pageWidth - 5, yPos, { align: "right" });
    yPos += 4;

    doc.line(5, yPos, pageWidth - 5, yPos);
    yPos += 6;

    doc.setFont("helvetica", "normal");
    const filteredItems = saleItems.filter(
      (item) =>
        item.productName !== "Discount" && item.productName !== "Bill Discount",
    );
    filteredItems.forEach((item) => {
      const splitName = doc.splitTextToSize(
        item.productName.toUpperCase(),
        pageWidth - 40,
      );
      doc.text(`${item.quantity} ${splitName[0]}`, 5, yPos);

      let nameY = yPos;
      if (splitName.length > 1) {
        for (let i = 1; i < splitName.length; i++) {
          nameY += 4;
          doc.text(splitName[i], 10, nameY);
        }
      }

      doc.text(item.rate.toFixed(2), 50, yPos, { align: "right" });
      doc.text((item.quantity * item.rate).toFixed(2), pageWidth - 5, yPos, {
        align: "right",
      });

      yPos = Math.max(yPos + 5, nameY + 5);
    });

    yPos += 2;
    doc.line(5, yPos, pageWidth - 5, yPos);
    yPos += 8;

    const finalTotal = total - discount;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    if (discount > 0) {
      doc.text("TOTAL BEFORE SAVINGS", 5, yPos);
      doc.text(`Rs. ${total.toFixed(2)}`, pageWidth - 5, yPos, {
        align: "right",
      });
      yPos += 6;

      doc.text("YOUR SAVINGS TODAY", 5, yPos);
      doc.text(`Rs. ${discount.toFixed(2)}`, pageWidth - 5, yPos, {
        align: "right",
      });
      yPos += 8;
    } else {
      doc.text("TOTAL", 5, yPos);
      doc.text(`Rs. ${total.toFixed(2)}`, pageWidth - 5, yPos, {
        align: "right",
      });
      yPos += 8;
    }

    doc.setFontSize(11);
    doc.text("NET PAYABLE", 5, yPos);
    doc.text(`Rs. ${finalTotal.toFixed(2)}`, pageWidth - 5, yPos, {
      align: "right",
    });
    yPos += 10;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("THANK YOU FOR SHOPPING!", pageWidth / 2, yPos, {
      align: "center",
    });
    yPos += 5;
    doc.text("Visit again soon", pageWidth / 2, yPos, { align: "center" });
    yPos += 8;

    if (businessVerified) {
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      // keep in sync with the DAILYHISAAB VERIFIED badge
      doc.text("**** DAILYHISAAB VERIFIED ****", pageWidth / 2, yPos, {
        align: "center",
      });
      yPos += 5;
      doc.setFont("helvetica", "normal");
    }

    doc.setFontSize(7);
    doc.setTextColor(0, 0, 255);
    doc.text("Generated by DailyHisaab", pageWidth / 2, yPos, {
      align: "center",
    });
    const textWidth = doc.getTextWidth("Generated by DailyHisaab");
    doc.link(pageWidth / 2 - textWidth / 2, yPos - 2, textWidth, 3, {
      url: "https://dailyhisaab.com",
    });
    doc.setTextColor(0, 0, 0);

    return doc;
  };

  const shareOnWhatsApp = (
    total: number,
    saleItems: SelectedItem[],
    discount: number = 0,
    orderId?: string,
  ) => {
    const bName = businessProfile?.businessName || "Retail Store";
    const itemsList = saleItems
      .map(
        (item) =>
          `• ${item.productName}: ${item.quantity} x ₹${item.rate} = ₹${(item.quantity * item.rate).toFixed(2)}`,
      )
      .join("\n");

    let businessInfo = `*${bName.toUpperCase()}*`;
    const finalTotal = total - discount;
    const discountText =
      discount > 0 ? `\nSavings: ₹${discount.toFixed(2)}` : "";

    const invoiceLink = orderId
      ? `\n\nView Invoice: https://${window.location.host}/invoice?orderId=${orderId}`
      : "";

    // keep in sync with the DAILYHISAAB VERIFIED badge
    const verifiedLine = businessVerified ? `\n**** DAILYHISAAB VERIFIED ****` : "";

    const message = `${businessInfo}\n\n*INVOICE*\nDate: ${new Date().toLocaleDateString()}\n--------------------------\n${itemsList}\n--------------------------${discountText}\n*NET PAYABLE: ₹${finalTotal.toFixed(2)}*${invoiceLink}\n\nThank you for shopping!${verifiedLine}\n_Generated by DailyHisaab_`;

    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  const handleQuantityChange = (
    productId: string,
    productName: string,
    rate: number,
    quantity: number,
  ) => {
    const newSelected = new Map(selectedItems);
    if (quantity === 0) {
      newSelected.delete(productId);
    } else {
      newSelected.set(productId, { productId, productName, rate, quantity });
    }
    setSelectedItems(newSelected);
  };

  const handleProductSelect = (product: Product) => {
    if (isScanning) {
      handleQuantityChange(
        product.id,
        product.name,
        product.salePrice || 0,
        (selectedItems.get(product.id)?.quantity || 0) + 1,
      );
    } else {
      setActionProduct(product);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("action") === "scan") {
      setIsBillingMode(true);
      window.history.replaceState({}, document.title, window.location.pathname);
      setTimeout(() => handleStartScan(), 500);
    }
  }, []);

  const handleToggleTorch = async () => {
    if (html5QrCodeRef.current) {
      try {
        const newTorchState = !isTorchOn;
        await html5QrCodeRef.current.applyVideoConstraints({
          advanced: [{ torch: newTorchState }]
        });
        setIsTorchOn(newTorchState);
      } catch (err) {
        console.error("Torch failed", err);
        toast({
          title: "Torch Unavailable",
          description: "Your device does not support browser flashlight control.",
          variant: "destructive"
        });
      }
    }
  };

  const handleStartScan = async () => {
    try {
      const { camera } = await BarcodeScanner.requestPermissions();

      if (camera === 'granted' || camera === 'limited') {
        setWasScanned(false);
        setIsScanning(true);
      } else {
        await NativeDialog.alert({
          title: 'Permission Required',
          message: 'Native camera permission is required to scan barcodes. Please enable it in your Android app settings.'
        });
      }
    } catch (err: any) {
      console.error("Native permission request failed", err);
      // Fallback allowing Html5Qrcode to prompt if running in a web browser during testing
      setWasScanned(false);
      setIsScanning(true);
    }
  };

  useEffect(() => {
    let html5QrCode: Html5Qrcode | null = null;
    let timerId: NodeJS.Timeout;

    if (isScanning) {
      const startScanner = async () => {
        try {
          html5QrCode = new Html5Qrcode("products-barcode-reader");
          html5QrCodeRef.current = html5QrCode;

          await html5QrCode.start(
            { facingMode: "environment" },
            {
              fps: 10,
              aspectRatio: 1.777778,
            },
            (decodedText) => {
              handleBarcodeScanned(decodedText);
            },
            () => { } // Ignore frame errors
          );
        } catch (err) {
          console.error("Camera Error:", err);
          setIsScanning(false);
          toast({
            title: "Camera Error",
            description: "Failed to initialize the camera feed.",
            variant: "destructive"
          });
        }
      };

      // Delay to allow DOM to mount `#products-barcode-reader`
      timerId = setTimeout(startScanner, 250);
    }

    return () => {
      if (timerId) clearTimeout(timerId);
      if (html5QrCode) {
        try {
          html5QrCode.stop().then(() => {
            html5QrCode?.clear();
          }).catch(() => { }); // Catch and swallow transition errors gracefully
        } catch (e) { }
      }
    };
  }, [isScanning]);

  const handleBarcodeScanned = async (barcode: string) => {
    const now = Date.now();
    const normalizedScannedBarcode = barcode.trim();

    if (normalizedScannedBarcode === lastScannedBarcodeRef.current) {
      if (now - lastScanTimeRef.current < 5000) return;
    } else {
      if (now - lastScanTimeRef.current < 500) return;
    }

    lastScannedBarcodeRef.current = normalizedScannedBarcode;
    lastScanTimeRef.current = now;

    try {
      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);

      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(880, audioCtx.currentTime);
      gainNode.gain.setValueAtTime(0.1, audioCtx.currentTime);

      oscillator.start();
      oscillator.stop(audioCtx.currentTime + 0.1);

      await Haptics.vibrate();
    } catch (e) {
      console.warn("Feedback failed", e);
    }

    const existingProduct = products.find((p) => {
      const pBarcode = String((p as any).barcode || "").trim();
      return pBarcode !== "" && pBarcode === normalizedScannedBarcode;
    });

    if (existingProduct) {
      if (!isBillingMode && userMode === "pro") {
        setWasScanned(true);
        setIsScanning(false);
        setIsTorchOn(false);
        setDuplicateProduct(existingProduct as Product);
      } else {
        setSelectedItems((prev) => {
          const newSelected = new Map(prev);
          const current = newSelected.get(existingProduct.id);
          const newQty = (current?.quantity || 0) + 1;
          newSelected.set(existingProduct.id, {
            productId: existingProduct.id,
            productName: existingProduct.name,
            rate: existingProduct.salePrice || 0,
            quantity: newQty,
          });
          toast({ title: `Added ${existingProduct.name} (Qty: ${newQty})` });
          return newSelected;
        });
      }
    } else if (isBillingMode) {
      setWasScanned(true);
      setIsScanning(false);
      setIsTorchOn(false);
      setNotInStoreBarcode(normalizedScannedBarcode);
    } else {
      setWasScanned(true);
      setIsScanning(false); // Triggers safe camera closure via useEffect cleanup
      setIsTorchOn(false);
      setIsSearching(true);
      setSearchStatus("Checking your shop records...");

      let autoName = "";
      let autoCategory = "General";
      try {
        const productInfo = (await fetchProductDataZeroCost(barcode, spreadsheetId || "")) as any;
        if (productInfo) {
          setSearchStatus("Product found!");
          setDataSource(productInfo.source || "external");
          autoName = productInfo.name;
          autoCategory = productInfo.category || "General";
          autoCategory = autoCategory.charAt(0).toUpperCase() + autoCategory.slice(1);
        } else {
          setSearchStatus("Not found. Please enter name manually.");
          setDataSource(null);
        }
      } catch (err) {
        setSearchStatus("Search failed.");
      } finally {
        setIsSearching(false);
      }

      setEditProductData({
        id: "",
        name: autoName,
        barcode: barcode,
        salePrice: undefined,
        costPrice: undefined,
        productType: "both",
        category: autoCategory,
        color: "#BBDEFB",
      } as Product);
      setIsAddDialogOpen(true);
      toast({ title: "New Item Detected!", description: autoName ? `Found: ${autoName}` : "We don't know this one yet. Quick Add it!" });
    }
  };

  const filteredProducts = useMemo(() => {
    if (!sortedProducts) return [];
    if (isScanning) return sortedProducts.filter((p) => selectedItems.has(p.id));
    if (!searchQuery.trim()) return sortedProducts;

    const query = searchQuery.toLowerCase();
    return sortedProducts.filter(
      (p) =>
        p.name.toLowerCase().includes(query) ||
        (p.category && p.category.toLowerCase().includes(query)) ||
        ((p as any).barcode && (p as any).barcode.toLowerCase().includes(query))
    );
  }, [sortedProducts, searchQuery, isScanning, selectedItems]);

  const handleSell = async (shouldShare = false) => {
    const items = Array.from(selectedItems.values());
    const total = items.reduce(
      (sum, item) => sum + item.quantity * item.rate,
      0,
    );
    setIsProcessing(true);
    try {
      const itemsToRecord = [...items];
      if (billDiscount > 0) {
        itemsToRecord.push({
          productId: "bill-discount",
          productName: "Bill Discount",
          quantity: 1,
          rate: -billDiscount,
        });
      }
      const recorded = await createTransactions(
        itemsToRecord,
        "sale",
        shouldShare,
        isGstBill,
      );
      const fullOrderId = recorded[0]?.fullOrderId;
      const orderId =
        fullOrderId || recorded[0]?.id.split("_").slice(0, 3).join("_");

      const processedItems = (items as any[]).map((item) => {
        const rec = (recorded as any[]).find((r) => r.productId === item.productId);
        return rec ? { ...item, total: rec.total, cgst: rec.cgst, sgst: rec.sgst } : item;
      });
      const processedTotal = processedItems.reduce(
        (sum: number, item: any) => sum + Number(item.total || item.quantity * item.rate),
        0,
      );

      const summary = items
        .map((i) => `${i.quantity}x ${i.productName}`)
        .join(", ");
      setLastInvoiceData({
        summary,
        total: processedTotal,
        items: processedItems,
        discount: billDiscount,
        orderId,
        fullOrderId,
        isGstBill: isGstBill,
      });
      setShowSharePopup(true);

      setSelectedItems(new Map());
      setBillDiscount(0);
      setIsGstBill(false);
      toast({ title: "Sale recorded successfully" });
    } catch (error: any) {
      toast({
        title: "Failed to record sale",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBuy = async () => {
    const items = Array.from(selectedItems.values()).map((item) => {
      const product = products.find((p) => p.id === item.productId);
      return {
        ...item,
        rate: product?.costPrice || item.rate,
      };
    });
    setIsProcessing(true);
    try {
      await createTransactions(items, "expense");
      setSelectedItems(new Map());
      toast({ title: "Expense recorded successfully" });
    } catch (error: any) {
      toast({
        title: "Failed to record expense",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const [showFavoritePrompt, setShowFavoritePrompt] = useState<{
    show: boolean;
    product: Product | null;
  }>({ show: false, product: null });
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);

  const handleAddProduct = async (data: any) => {
    setIsSaving(true);
    try {
      const productData = {
        ...data,
        salePrice: parseFloat(data.salePrice) || 0,
        costPrice: parseFloat(data.costPrice) || 0,
        rate: parseFloat(data.salePrice) || 0,
      };
      if (editProductData && editProductData.id) {
        const oldStock = (editProductData as any).stock || 0;
        const newStock = parseFloat(data.stock) || 0;
        const stockAdded = newStock - oldStock;
        await updateProduct({ ...editProductData, ...productData });
        toast({
          title: "Product Updated",
          description: `${productData.name || editProductData.name} has been updated.`
        });

        if (userMode === "pro" && stockAdded > 0 && productData.costPrice > 0) {
          try {
            await createStockExpense({
              productId: editProductData.id,
              productName: productData.name || editProductData.name,
              quantity: stockAdded,
              rate: productData.costPrice,
            });
          } catch (e) {
            console.error("Stock expense transaction failed:", e);
          }
        }
      } else {
        const isFromExternal =
          productData.isFromExternal ||
          dataSource === "external" ||
          dataSource === "firebase" ||
          wasScanned;
        const newProduct = await createProduct({
          ...productData,
          isFromExternal,
        });
        if (data.barcode && !isFromExternal) {
          try {
            await saveToMasterCatalog(data.barcode, {
              name: data.name,
              brand: data.category || "",
              img: "",
            });
          } catch (e) {
            console.error("Master catalog save failed:", e);
          }
        }
        toast({
          title: "Product Added",
          description: `${data.name} has been added successfully.`
        });
        // Natural break after adding a product — attempt a (capped) interstitial.
        triggerAd();

        if (
          userMode === "pro" &&
          (parseFloat(data.stock) || 0) > 0 &&
          productData.costPrice > 0
        ) {
          try {
            await createStockExpense({
              productId: newProduct.id,
              productName: productData.name,
              quantity: parseFloat(data.stock) || 0,
              rate: productData.costPrice,
            });
          } catch (e) {
            console.error("Stock expense transaction failed:", e);
          }
        }

        const skipPrompt =
          localStorage.getItem("skipFavoritePrompt") === "true";
        if (!skipPrompt && !data.isFavorite) {
          setShowFavoritePrompt({ show: true, product: newProduct });
        }
      }
      setIsAddDialogOpen(false);
      setEditProductData(null);
    } catch (error: any) {
      toast({
        title: editProductData
          ? "Failed to update product"
          : "Failed to add product",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleFavoriteConfirm = async (isFavorite: boolean) => {
    if (doNotShowAgain) {
      localStorage.setItem("skipFavoritePrompt", "true");
    }

    if (isFavorite && showFavoritePrompt.product) {
      try {
        await editProduct({ ...showFavoritePrompt.product, isFavorite: true });
        toast({
          title: "Added to Favorites",
          description: `${showFavoritePrompt.product.name} is now a favorite!`
        });
      } catch (error: any) {
        console.error("Failed to update favorite status", error);
      }
    }
    setShowFavoritePrompt({ show: false, product: null });
  };

  const handleEditProduct = (product: Product) => {
    setActionProduct(null); // Close action sheet
    setEditProductData(product);
    setIsAddDialogOpen(true);
  };

  const handleToggleFavorite = async (productId: string) => {
    const product = products.find((p) => p.id === productId);
    if (product) {
      try {
        const newStatus = !product.isFavorite;
        await editProduct({ ...product, isFavorite: newStatus });
        setShowFavoriteOverlay({ show: true, isFavorite: newStatus });
        setTimeout(
          () => setShowFavoriteOverlay({ show: false, isFavorite: false }),
          800,
        );
      } catch (error: any) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      }
    }
  };

  const handleBarcodeBluetooth = async () => {
    if (!user?.uid) {
      toast({ title: "Please wait for profile to load", variant: "destructive" });
      return;
    }

    setBtBarcodeStatus("connecting");
    setBtBarcodeError("");
    try {
      const printList = products.filter(p => !!p.barcode && (printFilter === "all" || p.barcode!.startsWith("DH")))
        .map(p => ({ name: p.name, barcode: p.barcode!, qty: productQtys[p.id] ?? printQty }))
        .filter(p => p.qty > 0);

      if (printList.length === 0) throw new Error("No products to print.");

      // 1. Generate Payload based on selected language
      let data: Uint8Array;
      if (labelPrinterLanguage === "tspl") {
        data = buildBarcodeTSPL(printList, labelWidth, labelHeight); // <-- PASS THEM HERE
      } else if (labelPrinterLanguage === "zpl") {
        data = buildBarcodeZPL(printList, labelWidth, labelHeight);  // <-- PASS THEM HERE
      } else {
        data = buildBarcodeEscPos(printList);
      }

      // Strict routing — barcodes go to the dedicated label slot only.
      // No fallback to receipt slot (TSPL/ZPL bytes corrupt receipt printer).
      const hasLabelPrinter = !!getCachedBTPrinter(user.uid, "label");
      if (!hasLabelPrinter) {
        setBtBarcodeStatus("error");
        setBtBarcodeError(
          "Connect a barcode printer first in Printer Setup → Barcode & Label."
        );
        return;
      }

      setBtBarcodeStatus("printing");
      try {
        await sendToBTPrinter(user.uid, data, "label");
      } catch (err: any) {
        if (err.message.includes("No") || err.message.includes("pair")) {
          await connectBTPrinter(user.uid, "label");
          await sendToBTPrinter(user.uid, data, "label");
        } else {
          throw err;
        }
      }

      setBtBarcodeStatus("success");
      setTimeout(() => { setBtBarcodeStatus("idle"); setShowPrintDialog(false); }, 2000);
    } catch (err: any) {
      setBtBarcodeStatus("error");
      setBtBarcodeError(err.message || "Print failed.");
    }
  };

  const handleBarcodeUsb = async () => {
    setUsbBarcodeStatus("connecting");
    setUsbBarcodeError("");
    try {
      const printList = products.filter(p => !!p.barcode && (printFilter === "all" || p.barcode!.startsWith("DH")))
        .map(p => ({ name: p.name, barcode: p.barcode!, qty: productQtys[p.id] ?? printQty }))
        .filter(p => p.qty > 0);

      if (printList.length === 0) throw new Error("No products to print.");

      // 1. Generate Payload based on selected language
      let data: Uint8Array;
      if (labelPrinterLanguage === "tspl") {
        data = buildBarcodeTSPL(printList, labelWidth, labelHeight); // <-- PASS THEM HERE
      } else if (labelPrinterLanguage === "zpl") {
        data = buildBarcodeZPL(printList, labelWidth, labelHeight);  // <-- PASS THEM HERE
      } else {
        data = buildBarcodeEscPos(printList);
      }

      // Strict routing — USB barcodes also go to the dedicated label slot only.
      const cachedLabel = getCachedUSBPrinter(user?.uid, "label");
      let device: any, endpoint: number;

      if (cachedLabel) {
        device = cachedLabel.device; endpoint = cachedLabel.endpoint;
      } else {
        // No cached label printer — request user to pick one.
        const conn = await connectUSBPrinter(user?.uid, "label");
        device = conn.device; endpoint = conn.endpoint;
      }

      setUsbBarcodeStatus("printing");
      await sendToUSBPrinter(device, endpoint, data, user?.uid);

      setUsbBarcodeStatus("success");
      setTimeout(() => { setUsbBarcodeStatus("idle"); setShowPrintDialog(false); }, 2000);
    } catch (err: any) {
      if (err?.name === "NotFoundError" || err?.message?.includes("No device selected") || err?.message?.includes("cancelled")) {
        setUsbBarcodeStatus("idle");
        return;
      }
      setUsbBarcodeStatus("error");
      setUsbBarcodeError(err.message || "USB print failed.");
    }
  };

  const handleUnifiedPrint = () => {
    if (!user?.uid) return;
    const hasBT = !!getCachedBTPrinter(user.uid, "label") || !!getCachedBTPrinter(user.uid, "receipt");
    const hasUSB = !!getCachedUSBPrinter(user.uid, "label") || !!getCachedUSBPrinter(user.uid, "receipt");

    if (hasBT) {
      handleBarcodeBluetooth();
    } else if (hasUSB) {
      handleBarcodeUsb();
    } else {
      setShowPrinterChoice(true);
    }
  };

  const handleDeleteProduct = async (productId: string) => {
    setActionProduct(null); // Close action sheet
    setProductToDeleteId(productId);
  };

  const confirmDeleteProduct = async () => {
    if (!productToDeleteId) return;
    try {
      await removeProduct(productToDeleteId);
      toast({ title: "Product deleted successfully" });
    } catch (error: any) {
      toast({
        title: "Error deleting product",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setProductToDeleteId(null);
    }
  };

  return (
    <div className="pb-36 bg-[#FFFBF2] dark:bg-[#1A1614] min-h-screen">
      {/* ── SEARCHING OVERLAY ── */}
      {isSearching && (
        <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm transition-all duration-300">
          <div className="relative">
            <div className="h-24 w-24 rounded-full border-t-4 border-b-4 border-primary animate-spin"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <Barcode className="h-10 w-10 text-primary animate-pulse" />
            </div>
          </div>
          <h2 className="mt-8 text-2xl font-bold text-white tracking-tight animate-bounce">
            Finding Product...
          </h2>
          <p className="mt-2 text-white/80 font-medium px-6 text-center">
            {searchStatus}
          </p>
          <div className="mt-6 flex gap-1">
            <div className="h-2 w-2 bg-primary rounded-full animate-bounce [animation-delay:-0.3s]"></div>
            <div className="h-2 w-2 bg-primary rounded-full animate-bounce [animation-delay:-0.15s]"></div>
            <div className="h-2 w-2 bg-primary rounded-full animate-bounce"></div>
          </div>
        </div>
      )}

      {/* ── DUPLICATE PRODUCT POPUP ── */}
      <AlertDialog
        open={!!duplicateProduct}
        onOpenChange={(open) => !open && setDuplicateProduct(null)}
      >
        <AlertDialogContent className="rounded-[2rem] border-none shadow-2xl max-w-sm">
          <div className="p-8 pb-6 text-center">
            <div className="w-16 h-16 bg-amber-50 dark:bg-amber-950/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <Barcode className="h-8 w-8 text-amber-600 dark:text-amber-400" />
            </div>
            <AlertDialogTitle className="text-2xl font-bold text-slate-800 dark:text-white mb-3 tracking-tight">
              Product Already Exists
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500 dark:text-gray-400 text-[15px] leading-relaxed px-2">
              <strong>{duplicateProduct?.name}</strong> is already in your
              store. Would you like to update this product?
            </AlertDialogDescription>
          </div>
          <div className="px-6 pb-6 flex flex-col gap-3">
            <AlertDialogAction
              onClick={() => {
                if (duplicateProduct) {
                  handleEditProduct(duplicateProduct);
                }
                setDuplicateProduct(null);
              }}
              className="w-full py-4 rounded-xl font-semibold text-[16px] text-white shadow-lg shadow-red-900/20"
              style={{
                background: "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)",
              }}
            >
              Yes, Update Product
            </AlertDialogAction>
            <AlertDialogCancel
              onClick={() => {
                setDuplicateProduct(null);
                handleStartScan();
              }}
              className="w-full py-4 rounded-xl font-semibold text-[16px] text-[#8B2121] dark:text-red-400 border-2 border-[#8B2121]/10 dark:border-white/10 bg-transparent"
            >
              No, Scan Next
            </AlertDialogCancel>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── NOT IN STORE POPUP (Billing Mode) ── */}
      <AlertDialog
        open={!!notInStoreBarcode}
        onOpenChange={(open) => !open && setNotInStoreBarcode(null)}
      >
        <AlertDialogContent className="rounded-[2rem] border-none shadow-2xl max-w-sm">
          <div className="p-8 pb-6 text-center">
            <div className="w-16 h-16 bg-red-50 dark:bg-red-950/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <Barcode className="h-8 w-8 text-red-600 dark:text-red-400" />
            </div>
            <AlertDialogTitle className="text-2xl font-bold text-slate-800 dark:text-white mb-3 tracking-tight">
              Product Not in Store
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500 dark:text-gray-400 text-[15px] leading-relaxed px-2">
              This product is not in your store product list. Would you like to
              check the external database and add it?
            </AlertDialogDescription>
          </div>
          <div className="px-6 pb-6 flex flex-col gap-3">
            <AlertDialogAction
              onClick={async () => {
                const barcode = notInStoreBarcode!;
                setNotInStoreBarcode(null);
                setIsSearching(true);
                setSearchStatus("Checking your shop records...");

                let autoName = "";
                let autoCategory = "General";
                try {
                  const productInfo = (await fetchProductDataZeroCost(
                    barcode,
                    spreadsheetId || "",
                  )) as any;

                  if (productInfo) {
                    setSearchStatus("Product found!");
                    setDataSource(productInfo.source || "external");
                    autoName = productInfo.name;
                    autoCategory = productInfo.category || "General";
                    autoCategory =
                      autoCategory.charAt(0).toUpperCase() +
                      autoCategory.slice(1);
                  } else {
                    setSearchStatus("Not found. Please enter name manually.");
                    setDataSource(null);
                  }
                } catch (err) {
                  console.error("Lookup error:", err);
                  setSearchStatus("Search failed.");
                } finally {
                  setIsSearching(false);
                }

                setEditProductData({
                  id: "",
                  name: autoName,
                  barcode: barcode,
                  salePrice: undefined,
                  costPrice: undefined,
                  productType: "both",
                  category: autoCategory,
                  color: "#BBDEFB",
                } as Product);
                setIsAddDialogOpen(true);

                toast({
                  title: "New Item Detected!",
                  description: autoName
                    ? `Found: ${autoName}`
                    : "We don't know this one yet. Quick Add it!",
                });
              }}
              className="w-full py-4 rounded-xl font-semibold text-[16px] text-white shadow-lg shadow-red-900/20"
              style={{
                background: "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)",
              }}
            >
              Check & Add Product
            </AlertDialogAction>
            <AlertDialogCancel
              onClick={() => {
                setNotInStoreBarcode(null);
                handleStartScan();
              }}
              className="w-full py-4 rounded-xl font-semibold text-[16px] text-[#8B2121] dark:text-red-400 border-2 border-[#8B2121]/10 dark:border-white/10 bg-transparent"
            >
              Skip
            </AlertDialogCancel>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── PRINT ALL BARCODES DIALOG ── */}
      <Dialog open={showPrintDialog} onOpenChange={setShowPrintDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] rounded-[24px] border-none p-0 overflow-hidden shadow-2xl bg-white dark:bg-slate-900 flex flex-col">
          <div className="px-5 pt-5 pb-3 flex items-center justify-between border-b border-slate-100 dark:border-slate-800 print:hidden">
            <h2 className="text-lg font-extrabold text-slate-900 dark:text-white tracking-tight">
              All Product Barcodes
            </h2>
            <div className="flex flex-col items-end gap-1">
              <button
                onClick={handleUnifiedPrint}
                disabled={
                  btBarcodeStatus === "connecting" || btBarcodeStatus === "printing" ||
                  usbBarcodeStatus === "connecting" || usbBarcodeStatus === "printing"
                }
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-white text-sm font-bold shadow-lg disabled:opacity-70 transition-all active:scale-95"
                style={{
                  background: (btBarcodeStatus === "success" || usbBarcodeStatus === "success")
                    ? "linear-gradient(135deg, #166534 0%, #14532d 100%)"
                    : "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)"
                }}
              >
                {(btBarcodeStatus === "connecting" || usbBarcodeStatus === "connecting" || btBarcodeStatus === "printing" || usbBarcodeStatus === "printing") && (
                  <Loader2 className="h-5 w-5 animate-spin" />
                )}
                {(btBarcodeStatus === "success" || usbBarcodeStatus === "success") && (
                  <CheckCircle2 className="h-5 w-5" />
                )}
                {(btBarcodeStatus === "error" || usbBarcodeStatus === "error") && (
                  <AlertCircle className="h-5 w-5" />
                )}
                {(btBarcodeStatus === "idle" && usbBarcodeStatus === "idle") && (
                  <Printer className="h-5 w-5" />
                )}

                {(btBarcodeStatus === "idle" && usbBarcodeStatus === "idle") && "Print Barcodes"}
                {(btBarcodeStatus === "connecting" || usbBarcodeStatus === "connecting") && "Connecting..."}
                {(btBarcodeStatus === "printing" || usbBarcodeStatus === "printing") && "Printing..."}
                {(btBarcodeStatus === "success" || usbBarcodeStatus === "success") && "Success!"}
                {(btBarcodeStatus === "error" || usbBarcodeStatus === "error") && "Retry Print"}
              </button>
              {(btBarcodeStatus === "error" || usbBarcodeStatus === "error") && (btBarcodeError || usbBarcodeError) && (
                <p className="text-xs text-red-500 font-bold max-w-[200px] text-right mt-1 leading-tight">
                  {btBarcodeError || usbBarcodeError}
                </p>
              )}
            </div>
          </div>
          {/* Control bar: filter + qty */}
          <div className="px-5 py-3 flex items-center justify-between border-b border-slate-100 dark:border-slate-800 gap-3 flex-wrap">
            <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-xl p-1">
              <button
                onClick={() => setPrintFilter("app")}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${printFilter === "app" ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow" : "text-slate-500 dark:text-slate-400"}`}
              >
                App Barcodes
              </button>
              <button
                onClick={() => setPrintFilter("all")}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${printFilter === "all" ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow" : "text-slate-500 dark:text-slate-400"}`}
              >
                All Barcodes
              </button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                Qty
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => {
                    setPrintQty((q) => Math.max(0, q - 1));
                    setProductQtys({});
                  }}
                  className="w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-white text-sm font-bold flex items-center justify-center"
                >
                  −
                </button>
                <span
                  className="w-7 text-center text-sm font-bold text-slate-800 dark:text-white"
                >
                  {printQty}
                </span>
                <button
                  onClick={() => {
                    setPrintQty((q) => Math.min(99, q + 1));
                    setProductQtys({});
                  }}
                  className="w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-white text-sm font-bold flex items-center justify-center"
                >
                  +
                </button>
              </div>
            </div>
          </div>
          <div
            id="print-barcode-grid"
            className="overflow-y-auto p-4 flex flex-col gap-3"
          >
            {products
              .filter(
                (p) =>
                  !!p.barcode &&
                  (printFilter === "all" || p.barcode!.startsWith("DH")),
              )
              .map((p) => {
                const cardQty = productQtys[p.id] ?? printQty;
                return (
                  <div
                    key={p.id}
                    className={`border rounded-xl p-3 flex flex-col items-center gap-1 transition-opacity ${cardQty === 0 ? "opacity-40 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800" : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"}`}
                  >
                    <p className="text-xs font-bold text-slate-700 dark:text-white text-center leading-tight">
                      {p.name}
                    </p>
                    <BarcodeRenderer
                      value={p.barcode!}
                      format="CODE128"
                      width={1.5}
                      height={55}
                      fontSize={9}
                      displayValue={false}
                    />
                    <p className="text-[10px] text-slate-500 dark:text-slate-400 font-mono tracking-wide">
                      {p.barcode}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] text-slate-400 dark:text-slate-500">
                        Qty
                      </span>
                      <button
                        onClick={() =>
                          setProductQtys((prev) => ({
                            ...prev,
                            [p.id]: Math.max(0, (prev[p.id] ?? printQty) - 1),
                          }))
                        }
                        className="w-6 h-6 rounded-md bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-white text-xs font-bold flex items-center justify-center"
                      >
                        −
                      </button>
                      <span
                        className="w-5 text-center text-xs font-bold text-slate-800 dark:text-white"
                      >
                        {cardQty}
                      </span>
                      <button
                        onClick={() =>
                          setProductQtys((prev) => ({
                            ...prev,
                            [p.id]: Math.min(99, (prev[p.id] ?? printQty) + 1),
                          }))
                        }
                        className="w-6 h-6 rounded-md bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-white text-xs font-bold flex items-center justify-center"
                      >
                        +
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── PRINTER SETUP REQUIRED DIALOG ── */}
      <Dialog open={showPrinterChoice} onOpenChange={setShowPrinterChoice}>
        <DialogContent className="sm:max-w-[340px] rounded-[32px] border-none p-6 shadow-2xl bg-[#FFFDF5] dark:bg-slate-900 text-center">
          <div className="w-16 h-16 bg-amber-50 dark:bg-amber-900/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-amber-100 dark:border-amber-800">
            <Printer className="h-8 w-8 text-amber-600 dark:text-amber-500" />
          </div>
          <DialogTitle className="text-2xl font-black text-slate-900 dark:text-white tracking-tight mb-2">
            Printer Not Set Up
          </DialogTitle>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-8 leading-relaxed px-2">
            To ensure your barcodes print correctly (and not as raw text), please run the quick Auto-Detect Wizard in your Printer Settings first.
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => {
                setShowPrinterChoice(false);
                setLocation("/printer-settings");
              }}
              className="w-full h-14 rounded-2xl font-black uppercase tracking-widest text-[12px] bg-[#8B1A1A] hover:bg-[#631212] text-white flex items-center justify-center gap-2 shadow-lg shadow-red-900/20 active:scale-95 transition-all"
            >
              Go to Printer Settings
            </button>
            <button
              onClick={() => setShowPrinterChoice(false)}
              className="w-full h-14 rounded-2xl font-black uppercase tracking-widest text-[12px] border-2 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center justify-center gap-2 active:scale-95 transition-all bg-transparent"
            >
              Cancel
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── STOCK MANAGER POPUP ── */}
      <Dialog open={showStockManager} onOpenChange={setShowStockManager}>
        <DialogContent className="sm:max-w-[420px] rounded-[28px] border-none p-0 overflow-hidden shadow-2xl bg-white dark:bg-slate-900">
          {/* Header */}
          <div className="px-5 pt-6 pb-3 flex items-center justify-between border-b border-slate-100 dark:border-slate-800">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-white"
                style={{
                  background:
                    "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)",
                }}
              >
                <Package className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-extrabold text-slate-900 dark:text-white tracking-tight">
                  Stock Manager
                </h2>
                <p className="text-[11px] text-slate-400 font-medium">
                  {atRiskProducts.length} items need restocking
                </p>
              </div>
            </div>
          </div>

          {/* Summary badges */}
          <div className="px-5 pt-3 pb-1 flex gap-2">
            <button
              onClick={() =>
                setStockFilter(stockFilter === "out" ? "all" : "out")
              }
              className={`px-3 py-1 rounded-full text-[11px] font-black transition-all active:scale-95 ${stockFilter === "out"
                ? "bg-red-500 text-white"
                : "bg-red-100 text-red-600"
                }`}
            >
              {outOfStockProducts.length} Out of Stock
            </button>
            <button
              onClick={() =>
                setStockFilter(stockFilter === "low" ? "all" : "low")
              }
              className={`px-3 py-1 rounded-full text-[11px] font-black transition-all active:scale-95 ${stockFilter === "low"
                ? "bg-orange-500 text-white"
                : "bg-orange-100 text-orange-600"
                }`}
            >
              {lowStockOnlyProducts.length} Low Stock
            </button>
          </div>

          {/* Bulk fill row */}
          <div className="px-5 pt-2 pb-2 flex items-center gap-2">
            <span className="text-xs font-bold text-slate-500 dark:text-slate-400 whitespace-nowrap">
              Bulk Fill All:
            </span>
            {[10, 25, 50, 99].map((val) => (
              <button
                key={val}
                onClick={() => handleBulkFill(val)}
                className="px-3 py-1 rounded-full text-[11px] font-black border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 active:scale-95 transition-transform"
              >
                +{val}
              </button>
            ))}
          </div>

          {/* Product list */}
          <div className="px-5 py-2 max-h-[45vh] overflow-y-auto space-y-2">
            {atRiskProducts.length === 0 ? (
              <div className="py-10 text-center">
                <Package className="h-12 w-12 text-slate-200 dark:text-slate-700 mx-auto mb-3" />
                <p className="text-base text-slate-400 font-medium">
                  All products are well stocked!
                </p>
              </div>
            ) : (
              atRiskProducts
                .filter((p) => {
                  if (stockFilter === "out") return (p as any).stock <= 0;
                  if (stockFilter === "low")
                    return (p as any).stock >= 1 && (p as any).stock <= ((p as any).lowStockAlert ?? 0);
                  return true;
                })
                .map((product) => {
                  const currentVal =
                    stockUpdates.get(product.id) ?? (product as any).stock ?? 0;
                  const isOut = (product as any).stock === 0;
                  return (
                    <div
                      key={product.id}
                      className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-2xl"
                    >
                      <div className="min-w-0 flex-1 mr-3">
                        <p className="font-bold text-sm text-slate-900 dark:text-white truncate">
                          {product.name}
                        </p>
                        <div className="flex items-center gap-1 mt-0.5">
                          <span
                            className={`text-[9px] font-black ${isOut ? "text-red-500" : "text-orange-500"}`}
                          >
                            {isOut ? "OUT OF STOCK" : "LOW STOCK"}
                          </span>
                          <span className="text-[9px] text-slate-400 font-medium">
                            · current: {(product as any).stock ?? 0}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          className="w-8 h-8 rounded-full bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 flex items-center justify-center shadow-sm active:scale-95 transition-transform"
                          onClick={() =>
                            setStockUpdates((prev) => {
                              const next = new Map(prev);
                              next.set(product.id, Math.max(0, currentVal - 1));
                              return next;
                            })
                          }
                        >
                          <Minus className="h-3.5 w-3.5 text-slate-600 dark:text-slate-300" />
                        </button>
                        <Input
                          type="number"
                          value={currentVal}
                          min={0}
                          onChange={(e) => {
                            const val = Math.max(
                              0,
                              parseInt(e.target.value) || 0,
                            );
                            setStockUpdates((prev) => {
                              const next = new Map(prev);
                              next.set(product.id, val);
                              return next;
                            });
                          }}
                          className="w-16 h-8 text-center font-bold text-sm rounded-xl border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700"
                        />
                        <button
                          className="w-8 h-8 rounded-full text-white flex items-center justify-center shadow-sm active:scale-95 transition-transform"
                          style={{
                            background:
                              "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)",
                          }}
                          onClick={() =>
                            setStockUpdates((prev) => {
                              const next = new Map(prev);
                              next.set(product.id, currentVal + 1);
                              return next;
                            })
                          }
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })
            )}
          </div>

          {/* Footer */}
          {atRiskProducts.length > 0 && (
            <div className="px-5 py-4 border-t border-slate-100 dark:border-slate-800">
              <Button
                className="w-full h-12 text-base font-bold rounded-2xl text-white shadow-lg"
                style={{
                  background:
                    "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)",
                }}
                onClick={handleSaveStocks}
                disabled={isSavingStock}
              >
                {isSavingStock
                  ? "Saving..."
                  : `Update Stock (${stockFilter === "out"
                    ? outOfStockProducts.length
                    : stockFilter === "low"
                      ? lowStockOnlyProducts.length
                      : atRiskProducts.length
                  } Products)`}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── HEADER ── */}
      <div className="sticky top-0 z-30 bg-[#FFFBF2]/90 dark:bg-[#1A1614]/90 backdrop-blur-md border-b border-slate-100 dark:border-slate-800 px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Logo size="md" />
          </div>
          <div className="flex items-center gap-2">
            <button
              className="w-10 h-10 rounded-full bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 flex items-center justify-center shadow-sm"
              onClick={() => setShowPrintDialog(true)}
            >
              <Printer className="h-5 w-5 text-slate-600 dark:text-slate-300" />
            </button>
            <button
              className="w-10 h-10 rounded-full flex items-center justify-center text-white shadow-lg"
              style={{
                background: "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)",
              }}
              onClick={() => setShowChoiceDialog(true)}
            >
              <Plus className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Search Bar */}
        {!isScanning && (
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <Input
              placeholder={
                userMode === "pro"
                  ? "Search premium inventory..."
                  : "Search products..."
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-11 pr-11 h-12 w-full rounded-2xl bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-sm font-medium shadow-sm"
            />
            {userMode === "pro" && (
              <button
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                onClick={() =>
                  isScanning ? setIsScanning(false) : handleStartScan()
                }
              >
                <Barcode className="h-5 w-5" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── INLINE SCANNER (Restored and fixed race condition) ── */}
      {isScanning && (
        <div className="px-4 pt-4">
          <div className="relative h-48 w-full overflow-hidden rounded-2xl border bg-black shadow-lg">
            <div id="products-barcode-reader" className="h-full w-full" />
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="relative h-32 w-64 border-2 border-white/50 rounded-sm overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-0.5 bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.8)] animate-[scan_2s_linear_infinite]" />
              </div>
            </div>

            {/* TORCH BUTTON */}
            <button
              onClick={handleToggleTorch}
              className="absolute bottom-3 right-3 p-2.5 bg-black/40 backdrop-blur-md border border-white/20 rounded-full text-white pointer-events-auto active:scale-90 transition-all z-50"
            >
              <Flashlight className={`h-5 w-5 ${isTorchOn ? 'text-amber-400 fill-amber-400' : 'text-white'}`} />
            </button>

            {/* CLOSE SCANNER BUTTON */}
            <button
              onClick={() => { setIsScanning(false); setIsTorchOn(false); }}
              className="absolute top-3 right-3 p-2.5 bg-black/40 backdrop-blur-md border border-white/20 rounded-full text-white pointer-events-auto active:scale-90 transition-all z-50"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Manual Add Search in Scanner Mode */}
          <div className="mt-3 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <Input
              placeholder="Search & add product manually..."
              value={scannerManualSearch}
              onChange={(e) => setScannerManualSearch(e.target.value)}
              className="pl-10 pr-4 h-12 w-full rounded-xl bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-sm font-medium shadow-sm"
            />
          </div>

          {/* Manual Search Results */}
          {scannerManualSearch.trim() && (
            <div className="mt-2 bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden shadow-sm max-h-48 overflow-y-auto">
              {products
                .filter((p) => {
                  const q = scannerManualSearch.toLowerCase();
                  return (
                    p.name.toLowerCase().includes(q) ||
                    (p.category && p.category.toLowerCase().includes(q)) ||
                    ((p as any).barcode &&
                      (p as any).barcode.toLowerCase().includes(q))
                  );
                })
                .slice(0, 8)
                .map((product) => (
                  <div
                    key={product.id}
                    className="flex items-center justify-between px-4 py-2.5 border-b border-slate-50 dark:border-slate-800 last:border-b-0 active:bg-slate-50 dark:active:bg-slate-800 cursor-pointer"
                    onClick={() => {
                      handleProductSelect(product as Product);
                      setScannerManualSearch("");
                      toast({ title: `${product.name} added to cart` });
                    }}
                  >
                    <div className="min-w-0">
                      <p className="font-bold text-sm text-slate-900 dark:text-white truncate">
                        {product.name}
                      </p>
                      <p className="text-xs text-slate-400">
                        {product.category || "General"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-slate-900 dark:text-white">
                        ₹{Number(product.salePrice).toFixed(0)}
                      </span>
                      <div
                        className="w-7 h-7 rounded-full flex items-center justify-center text-white"
                        style={{
                          background:
                            "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)",
                        }}
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}

          <Button
            variant="default"
            className="w-full h-12 text-lg font-bold shadow-lg mt-3 rounded-2xl"
            onClick={() => setShowReviewPopup(true)}
          >
            <ShoppingCart className="h-5 w-5 mr-2" />
            Review Items ({selectedItems.size})
          </Button>
          <style>{`
            @keyframes scan {
              0%, 100% { top: 0%; }
              50% { top: 100%; }
            }
            #products-barcode-reader video {
              object-fit: cover !important;
              height: 100% !important;
            }
            #qr-shaded-region, #products-barcode-reader svg {
              display: none !important;
            }
          `}</style>
        </div>
      )}

      {/* ── REVIEW ITEMS POPUP ── */}
      <Dialog open={showReviewPopup} onOpenChange={setShowReviewPopup}>
        <DialogContent className="sm:max-w-[420px] rounded-[28px] border-none p-0 overflow-hidden shadow-2xl bg-white dark:bg-slate-900">
          <div className="px-5 pt-6 pb-3 flex items-center justify-between border-b border-slate-100 dark:border-slate-800">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-white"
                style={{
                  background:
                    "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)",
                }}
              >
                <ShoppingCart className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-extrabold text-slate-900 dark:text-white tracking-tight">
                  Cart Review
                </h2>
                <p className="text-[11px] text-slate-400 font-medium">
                  {selectedItems.size}{" "}
                  {selectedItems.size === 1 ? "item" : "items"} in cart
                </p>
              </div>
            </div>
          </div>

          <div className="px-5 py-3 max-h-[50vh] overflow-y-auto">
            {selectedItems.size === 0 ? (
              <div className="py-10 text-center">
                <ShoppingCart className="h-12 w-12 text-slate-200 dark:text-slate-700 mx-auto mb-3" />
                <p className="text-base text-slate-400 font-medium">
                  Your cart is empty
                </p>
                <p className="text-xs text-slate-300 dark:text-slate-600 mt-1">
                  Scan or search products to add
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {Array.from(selectedItems.values()).map((item) => (
                  <div
                    key={item.productId}
                    className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-2xl"
                  >
                    <div className="min-w-0 flex-1 mr-3">
                      <p className="font-bold text-sm text-slate-900 dark:text-white truncate">
                        {item.productName}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        ₹{Number(item.rate).toFixed(2)} each
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        className="w-8 h-8 rounded-full bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 flex items-center justify-center shadow-sm active:scale-95 transition-transform"
                        onClick={() =>
                          handleQuantityChange(
                            item.productId,
                            item.productName,
                            item.rate,
                            item.quantity - 1,
                          )
                        }
                      >
                        <Minus className="h-3.5 w-3.5 text-slate-600 dark:text-slate-300" />
                      </button>
                      <span className="w-6 text-center font-bold text-sm text-slate-900 dark:text-white">
                        {item.quantity}
                      </span>
                      <button
                        className="w-8 h-8 rounded-full text-white flex items-center justify-center shadow-sm active:scale-95 transition-transform"
                        style={{
                          background:
                            "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)",
                        }}
                        onClick={() =>
                          handleQuantityChange(
                            item.productId,
                            item.productName,
                            item.rate,
                            item.quantity + 1,
                          )
                        }
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <p className="w-20 text-right font-bold text-sm text-slate-900 dark:text-white">
                      ₹{(item.quantity * item.rate).toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {selectedItems.size > 0 && (
            <div className="px-5 py-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
                  Total (
                  {Array.from(selectedItems.values()).reduce(
                    (sum, i) => sum + i.quantity,
                    0,
                  )}{" "}
                  items)
                </span>
                <span className="text-xl font-extrabold text-slate-900 dark:text-white">
                  ₹
                  {(
                    Array.from(selectedItems.values()).reduce(
                      (sum, i) => sum + i.quantity * i.rate,
                      0,
                    ) - (isBillingMode ? billDiscount : 0)
                  ).toFixed(2)}
                </span>
              </div>
              {isBillingMode && (
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-sm font-medium whitespace-nowrap text-slate-600 dark:text-slate-400">
                    Bill Discount:
                  </span>
                  <Input
                    type="number"
                    placeholder="₹ 0.00"
                    value={billDiscount || ""}
                    onChange={(e) => setBillDiscount(Number(e.target.value))}
                    className="h-9"
                  />
                </div>
              )}
              {isBillingMode && userMode === "pro" && (
                <div className="flex items-center justify-between mb-3 px-1">
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    GST Bill
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isGstBill}
                    onClick={() => setIsGstBill(!isGstBill)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isGstBill ? "bg-[#8B1A1A]" : "bg-slate-300 dark:bg-slate-600"}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isGstBill ? "translate-x-6" : "translate-x-1"}`}
                    />
                  </button>
                </div>
              )}
              <Button
                className="w-full h-12 text-base font-bold rounded-2xl text-white shadow-lg"
                style={{
                  background:
                    "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)",
                }}
                onClick={() => {
                  if (isBillingMode) {
                    setShowReviewPopup(false);
                    handleSell(false);
                  } else {
                    setShowReviewPopup(false);
                  }
                }}
                disabled={isBillingMode && isProcessing}
              >
                {isBillingMode
                  ? isProcessing
                    ? "Processing..."
                    : `Record Sale (₹${(Array.from(selectedItems.values()).reduce((sum, i) => sum + i.quantity * i.rate, 0) - billDiscount).toFixed(2)})`
                  : "Continue Scanning"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <div className="px-4 pt-4 space-y-4">
        {/* ── PRO STAT WIDGET (Compact Mini-Dashboard) ── */}
        {userMode === "pro" && !isScanning && (
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 shadow-sm border border-slate-100 dark:border-slate-800 mb-4 flex items-center justify-between">
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-0.5">Inventory Value</p>
              <p className="text-xl font-black text-slate-900 dark:text-white leading-none flex items-center gap-1.5">
                ₹{products.reduce((sum, p) => sum + (p.salePrice || 0) * (p.stock || 0), 0).toLocaleString("en-IN")}
                <TrendingUp className="h-3 w-3 text-emerald-500" />
              </p>
            </div>
            <div className={`flex items-center gap-2 px-3 py-2 rounded-xl cursor-pointer transition-colors ${atRiskProducts.length > 0 ? 'bg-red-50 dark:bg-red-900/20' : 'bg-slate-50 dark:bg-slate-800'}`} onClick={openStockManager}>
              <div className={`h-8 w-8 rounded-full flex items-center justify-center ${atRiskProducts.length > 0 ? 'bg-red-100 text-red-600 dark:bg-red-900/50 dark:text-red-400' : 'bg-white dark:bg-slate-700 text-slate-500 dark:text-slate-400 shadow-sm'}`}>
                {atRiskProducts.length > 0 ? <AlertCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
              </div>
              <div>
                <p className={`text-[10px] font-black uppercase tracking-widest ${atRiskProducts.length > 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-500 dark:text-slate-400'}`}>
                  {atRiskProducts.length > 0 ? 'Needs Restock' : 'Stock Healthy'}
                </p>
                <p className={`text-xs font-bold ${atRiskProducts.length > 0 ? 'text-red-700 dark:text-red-300' : 'text-slate-700 dark:text-slate-300'}`}>
                  {atRiskProducts.length} Items
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ── SECTION HEADER ── */}
        {!isScanning && (
          <div className="flex items-center justify-between mt-2">
            <p className="text-[11px] font-black uppercase tracking-widest text-slate-400">
              {userMode === "pro" ? "DETAILED INVENTORY" : "PRODUCTS"}
            </p>
            <p className="text-[11px] font-bold text-slate-400">
              {filteredProducts.length} SKUs
            </p>
          </div>
        )}

        {/* ── CATEGORY FILTER CHIPS ── */}
        {userMode === "pro" && !isScanning && (
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {["All", ...Array.from(new Set(products.map((p) => p.category).filter(Boolean)))].map((cat) => (
              <button
                key={cat}
                onClick={() => setSearchQuery(cat === "All" ? "" : cat || "")}
                className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-bold transition-all ${(cat === "All" && !searchQuery) || searchQuery === cat ? "text-white shadow-md" : "bg-white dark:bg-slate-800 text-slate-500 border border-slate-200 dark:border-slate-700"}`}
                style={(cat === "All" && !searchQuery) || searchQuery === cat ? { background: "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)" } : {}}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {/* ── HIGH DENSITY PRODUCT LIST ── */}
        {isLoading ? (
          <div className="space-y-3"><ProductCardSkeleton /><ProductCardSkeleton /></div>
        ) : filteredProducts.length > 0 ? (
          <>
            <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-100 dark:border-slate-800 overflow-hidden divide-y divide-slate-100 dark:divide-slate-800">
              {filteredProducts.slice(0, pageSize).map((product) => {
                const qtyInCart = selectedItems.get(product.id)?.quantity || 0;

                // Strict Color Dot Visual Logic
                const hasStockInfo = product.stock !== undefined;
                const isOutOfStock = hasStockInfo && product.stock <= 0;
                const isLowStock = hasStockInfo && product.stock > 0 && product.stock <= (product.lowStockAlert ?? 0);
                const stockDotColor = isOutOfStock ? 'bg-red-500' : isLowStock ? 'bg-amber-500' : 'bg-emerald-500';

                return (
                  <div
                    key={product.id}
                    className={`p-3.5 flex items-center justify-between transition-colors ${isScanning ? 'active:bg-slate-50 dark:active:bg-slate-800/50' : 'cursor-pointer active:bg-slate-50 dark:active:bg-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/50'}`}
                    onClick={() => handleProductSelect(product as Product)}
                  >
                    <div className="flex items-center gap-3.5 min-w-0 pr-4">
                      {/* Compact Avatar */}
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 text-base font-black shadow-inner" style={{ backgroundColor: product.color ? `${product.color}20` : '#F1F5F9', color: product.color || '#64748B' }}>
                        {product.name.charAt(0).toUpperCase()}
                      </div>

                      <div className="min-w-0">
                        <p className="font-bold text-sm text-slate-900 dark:text-white truncate">
                          {product.name}
                        </p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
                            {product.category || "General"}
                          </span>

                          {/* Visual Dot & Bold Number Logic */}
                          {userMode === "pro" && hasStockInfo && (
                            <>
                              <span className="text-[8px] text-slate-300 dark:text-slate-600">•</span>
                              <span className="flex items-center gap-1.5">
                                <div className={`w-2 h-2 rounded-full shadow-sm ${stockDotColor}`} />
                                <span className="text-[11px] font-bold text-slate-600 dark:text-slate-300">
                                  {product.stock}
                                </span>
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                      {/* Stacked Pricing: SP & CP */}
                      <div className="flex flex-col items-end">
                        <div className="flex items-center gap-1">
                          <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">SP</span>
                          <span className="text-sm font-black text-slate-900 dark:text-white">₹{Number(product.salePrice).toFixed(0)}</span>
                        </div>
                        {userMode === "pro" && product.costPrice !== undefined && product.costPrice > 0 && (
                          <div className="flex items-center gap-1">
                            <span className="text-[8px] font-bold text-slate-400 uppercase tracking-wider">CP</span>
                            <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400">₹{Number(product.costPrice).toFixed(0)}</span>
                          </div>
                        )}
                      </div>

                      {/* Quick Add/Subtract Buttons (Scanning Mode) */}
                      {isScanning && (
                        <div className="flex items-center gap-1 ml-1" onClick={e => e.stopPropagation()}>
                          {qtyInCart > 0 ? (
                            <>
                              <button onClick={() => handleQuantityChange(product.id, product.name, product.salePrice || 0, qtyInCart - 1)} className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-slate-600 dark:text-slate-300 active:scale-95 transition-transform"><Minus className="h-4 w-4" /></button>
                              <span className="w-5 text-center text-sm font-bold text-slate-900 dark:text-white">{qtyInCart}</span>
                              <button onClick={() => handleQuantityChange(product.id, product.name, product.salePrice || 0, qtyInCart + 1)} className="w-8 h-8 rounded-full bg-[#8B1A1A] text-white flex items-center justify-center active:scale-95 transition-transform"><Plus className="h-4 w-4" /></button>
                            </>
                          ) : (
                            <button onClick={() => handleQuantityChange(product.id, product.name, product.salePrice || 0, 1)} className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-[#8B1A1A] active:scale-95 transition-transform border border-slate-200 dark:border-slate-700">
                              <Plus className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination Controls */}
            {filteredProducts.length > 25 && (
              <div className="flex gap-2 justify-center pt-2 flex-wrap">
                {(() => {
                  const total = filteredProducts.length;
                  const options = [25];
                  if (total > 25) options.push(50);
                  if (total > 50) options.push(100);

                  let next = 125;
                  while (next < total) {
                    options.push(next);
                    next += 25; // Scales infinitely in steps of 25!
                  }
                  // Ensure the final bucket is available to show all remaining products
                  if (total > 100 && total > options[options.length - 1]) {
                    options.push(next);
                  }

                  return options;
                })().map(size => (
                  <button
                    key={size}
                    onClick={() => setPageSize(size)}
                    className={`px-4 py-2 rounded-2xl text-sm font-bold shadow-sm transition-colors ${pageSize === size ? "bg-[#8B1A1A] text-white border border-[#8B1A1A]" : "bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700"}`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <EmptyState icon={Package} title="No products yet" description="Add your first product to start billing" action={{ label: "Add Product", onClick: () => setIsAddDialogOpen(true) }} />
        )}
      </div>

      {/* ── BOTTOM SHEET: PRODUCT ACTIONS ── */}
      <Dialog open={!!actionProduct} onOpenChange={(open) => !open && setActionProduct(null)}>
        <DialogContent className="sm:max-w-[420px] rounded-[28px] border-none p-0 overflow-hidden shadow-2xl bg-white dark:bg-slate-900 mt-auto mb-0 sm:m-auto">
          {actionProduct && (
            <>
              <div className="p-6 text-center border-b border-slate-100 dark:border-slate-800">
                <div
                  className="w-16 h-16 mx-auto rounded-2xl mb-3 flex items-center justify-center text-2xl font-black shadow-inner"
                  style={{
                    backgroundColor: actionProduct.color ? `${actionProduct.color}20` : '#F1F5F9',
                    color: actionProduct.color || '#64748B'
                  }}
                >
                  {actionProduct.name.charAt(0).toUpperCase()}
                </div>
                <h2 className="text-xl font-extrabold text-slate-900 dark:text-white mb-1 tracking-tight">
                  {actionProduct.name}
                </h2>
                <div className="flex justify-center items-center gap-2 mt-2">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-md border border-slate-200 dark:border-slate-700">
                    {actionProduct.category || "General"}
                  </span>
                  <span className="text-[10px] font-black uppercase tracking-widest text-teal-600 bg-teal-50 dark:bg-teal-900/30 px-2 py-0.5 rounded-md">
                    {Number(actionProduct.salePrice) > 0 && Number(actionProduct.costPrice) > 0
                      ? "Sale & Expense"
                      : Number(actionProduct.costPrice) > 0 && !(Number(actionProduct.salePrice) > 0)
                        ? "For Expense"
                        : "For Sale"}
                  </span>
                </div>

                <div className="flex justify-center items-center gap-8 mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Sale Price</p>
                    <p className="text-lg font-black text-slate-900 dark:text-white">₹{Number(actionProduct.salePrice || 0).toFixed(0)}</p>
                  </div>
                  <div className="w-px h-8 bg-slate-200 dark:bg-slate-800"></div>
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Cost Price</p>
                    <p className="text-lg font-black text-slate-500 dark:text-slate-400">₹{Number(actionProduct.costPrice || 0).toFixed(0)}</p>
                  </div>
                </div>
              </div>
              <div className="p-4 space-y-2">
                <button
                  onClick={() => {
                    handleEditProduct(actionProduct);
                    setWasScanned(false);
                  }}
                  className="w-full flex items-center gap-3 p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 active:bg-slate-100 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600">
                    <Edit2 className="h-4 w-4" />
                  </div>
                  <span className="font-bold text-slate-700 dark:text-slate-200">Edit Details</span>
                </button>

                <button
                  onClick={() => {
                    handleToggleFavorite(actionProduct.id);
                    setActionProduct(null);
                  }}
                  className="w-full flex items-center gap-3 p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 active:bg-slate-100 transition-colors"
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${(actionProduct as any).isFavorite ? 'bg-red-100 dark:bg-red-900/30 text-red-600' : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'}`}>
                    <Heart className={`h-4 w-4 ${(actionProduct as any).isFavorite ? 'fill-red-600 text-red-600' : ''}`} />
                  </div>
                  <span className="font-bold text-slate-700 dark:text-slate-200">{(actionProduct as any).isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}</span>
                </button>

                <button
                  onClick={() => handleDeleteProduct(actionProduct.id)}
                  className="w-full flex items-center gap-3 p-4 rounded-2xl bg-red-50 dark:bg-red-900/10 active:bg-red-100 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </div>
                  <span className="font-bold text-red-600 dark:text-red-400">Delete Product</span>
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ── BILLING TOTAL (Normal mode only) ── */}
      {userMode !== "pro" && selectedItems.size > 0 && (
        <div className="px-4 pb-4 mt-4">
          <div className="p-4 border rounded-2xl bg-white dark:bg-slate-900 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Order Total:</span>
              <span className="font-bold">
                ₹
                {(
                  Array.from(selectedItems.values()).reduce(
                    (sum, i) => sum + i.quantity * i.rate,
                    0,
                  ) - billDiscount
                ).toFixed(2)}
              </span>
            </div>
            <div className="flex items-center justify-end gap-2 text-xs text-muted-foreground mb-4">
              <Zap className="h-3 w-3 text-amber-500 fill-amber-500" />
              <span className="text-amber-600 font-medium">
                Reward: +1 Point
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium whitespace-nowrap">
                Bill Discount:
              </span>
              <Input
                type="number"
                placeholder="₹ 0.00"
                value={billDiscount || ""}
                onChange={(e) => setBillDiscount(Number(e.target.value))}
                className="h-8"
              />
            </div>
          </div>
        </div>
      )}

      {userMode !== "pro" && (
        <BillingBar
          selectedItems={Array.from(selectedItems.values())}
          onSell={() => handleSell(false)}
          onBuy={handleBuy}
          isProcessing={isProcessing}
          discount={billDiscount}
          onSellAndShare={
            userMode === "pro" ? () => handleSell(true) : undefined
          }
        />
      )}

      {/* New Product Choice Dialog */}
      <Dialog open={showChoiceDialog} onOpenChange={setShowChoiceDialog}>
        <DialogContent className="sm:max-w-[380px] rounded-[32px] border-none p-0 overflow-hidden shadow-2xl bg-white dark:bg-slate-900">
          <div className="pt-10 pb-2 px-6 text-center relative">
            <div
              className="mb-4 inline-flex items-center justify-center w-14 h-14 rounded-2xl"
              style={{ backgroundColor: "rgba(139, 26, 26, 0.05)" }}
            >
              <Sparkles className="h-8 w-8" style={{ color: "#8B1A1A" }} />
            </div>
            <h2 className="text-slate-800 dark:text-white text-3xl font-extrabold tracking-tight mb-2">
              New Product
            </h2>
            <p
              className="text-xs font-bold tracking-[0.15em] uppercase mb-8"
              style={{ color: "#8B1A1A" }}
            >
              Earn Rewards for Scanning!
            </p>
          </div>
          <div className="px-6 pb-6">
            <button
              className="w-full group relative p-10 rounded-[28px] text-white transition-all active:scale-[0.98] shadow-2xl mb-8 overflow-hidden"
              style={{
                background: "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)",
                boxShadow: "0 20px 40px rgba(139, 26, 26, 0.3)",
              }}
              onClick={() => {
                setShowChoiceDialog(false);
                handleStartScan();
              }}
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-white/20"></div>
              <div
                className="absolute top-5 right-5 px-3 py-1 rounded-full shadow-sm flex items-center gap-1 border border-white/20"
                style={{ backgroundColor: "#D4AF37" }}
              >
                <span
                  className="text-xs font-black tracking-tighter"
                  style={{ color: "#8B1A1A" }}
                >
                  +5 PTS
                </span>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="mb-5 w-20 h-20 rounded-3xl bg-white/10 flex items-center justify-center ring-4 ring-white/10 backdrop-blur-sm">
                  <img
                    src={barcodeIcon}
                    alt="Barcode"
                    className="h-12 w-12 invert"
                  />
                </div>
                <span className="text-2xl font-extrabold tracking-tight">
                  Scan Barcode
                </span>
                <span className="text-sm text-white/70 mt-2 font-medium">
                  Quick & Easy Scanning
                </span>
              </div>
            </button>

            <div className="relative flex py-2 items-center mb-6">
              <div className="flex-grow border-t border-gray-100 dark:border-slate-800"></div>
              <span className="flex-shrink mx-4 text-xs text-slate-400 dark:text-slate-500 font-bold tracking-widest uppercase">
                Or
              </span>
              <div className="flex-grow border-t border-gray-100 dark:border-slate-800"></div>
            </div>

            <div className="flex flex-col items-center gap-6">
              <button
                className="flex items-center gap-3 px-6 py-3 rounded-full border transition-colors group"
                style={{
                  borderColor: "rgba(139, 26, 26, 0.1)",
                  backgroundColor: "rgba(139, 26, 26, 0.05)",
                  color: "#8B1A1A",
                }}
                onClick={() => {
                  setShowChoiceDialog(false);
                  setEditProductData(null);
                  setIsAddDialogOpen(true);
                }}
              >
                <Keyboard className="h-5 w-5" />
                <span className="font-bold text-sm">Manual Add</span>
                <span
                  className="px-2 py-0.5 rounded-full text-[9px] font-black"
                  style={{ backgroundColor: "rgba(139, 26, 26, 0.1)" }}
                >
                  +1 PTS
                </span>
              </button>
              <div className="text-center px-4">
                <p className="text-[11px] text-slate-400 font-medium leading-relaxed">
                  Help us build the catalog and get exclusive benefits.
                </p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AddProductDialog
        open={isAddDialogOpen}
        onOpenChange={(open) => {
          setIsAddDialogOpen(open);
          if (!open) setEditProductData(null);
        }}
        onSubmit={handleAddProduct}
        editProduct={editProductData}
        isLoading={isSaving}
      />

      <AlertDialog
        open={showFavoritePrompt.show}
        onOpenChange={(open) =>
          !open && setShowFavoritePrompt({ show: false, product: null })
        }
      >
        <AlertDialogContent className="rounded-[2rem] border-none shadow-2xl max-w-sm">
          <div className="p-8 pb-6 text-center">
            <div className="w-16 h-16 bg-amber-50 dark:bg-amber-950/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart className="h-8 w-8 text-amber-600 dark:text-amber-400" />
            </div>
            <AlertDialogTitle className="text-2xl font-bold text-amber-700 dark:text-amber-400 mb-3 tracking-tight">
              Add to Favorites?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500 dark:text-gray-400 text-[15px] leading-relaxed px-2">
              Would you like to add{" "}
              <strong>{showFavoritePrompt.product?.name}</strong> to your
              favorites for quick access?
            </AlertDialogDescription>
          </div>
          <div className="flex items-center justify-center space-x-2 px-6 pb-2">
            <Checkbox
              id="skip-prompt"
              checked={doNotShowAgain}
              onCheckedChange={(checked) => setDoNotShowAgain(checked === true)}
            />
            <label
              htmlFor="skip-prompt"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Do not show this again
            </label>
          </div>
          <div className="px-6 pb-6 flex flex-col gap-3">
            <AlertDialogAction
              onClick={() => handleFavoriteConfirm(true)}
              className="w-full py-4 rounded-xl font-semibold text-[16px] text-white shadow-lg shadow-red-900/20"
              style={{
                background: "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)",
              }}
            >
              Yes, Add to Favorites
            </AlertDialogAction>
            <AlertDialogCancel
              onClick={() => handleFavoriteConfirm(false)}
              className="w-full py-4 rounded-xl font-semibold text-[16px] text-[#8B2121] dark:text-red-400 border-2 border-[#8B2121]/10 dark:border-white/10 bg-transparent"
            >
              No
            </AlertDialogCancel>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      <AnimatePresence>
        {showFavoriteOverlay.show && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-center justify-center bg-black/20 pointer-events-none"
          >
            <motion.div
              initial={{ scale: 0, rotate: -45 }}
              animate={{ scale: 1.5, rotate: 0 }}
              exit={{ scale: 0, rotate: 45 }}
              transition={{ type: "spring", stiffness: 300, damping: 15 }}
            >
              <Heart
                className={`h-32 w-32 ${showFavoriteOverlay.isFavorite
                  ? "fill-red-500 text-red-500"
                  : "text-white fill-white/20"
                  } drop-shadow-2xl`}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AlertDialog
        open={!!productToDeleteId}
        onOpenChange={(open) => !open && setProductToDeleteId(null)}
      >
        <AlertDialogContent className="rounded-[2rem] border-none shadow-2xl max-w-sm">
          <div className="p-8 pb-6 text-center">
            <div className="w-16 h-16 bg-red-50 dark:bg-red-950/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <Trash2 className="h-8 w-8 text-[#8B2121] dark:text-red-400" />
            </div>
            <AlertDialogTitle className="text-2xl font-bold text-[#8B2121] dark:text-red-400 mb-3 tracking-tight">
              Delete Product?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500 dark:text-gray-400 text-[15px] leading-relaxed px-2">
              This will <strong>permanently delete</strong> this product from
              your inventory.
            </AlertDialogDescription>
          </div>
          <div className="px-6 pb-6 flex flex-col gap-3">
            <AlertDialogAction
              onClick={confirmDeleteProduct}
              className="w-full py-4 rounded-xl font-semibold text-[16px] text-white shadow-lg shadow-red-900/20"
              style={{
                background: "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)",
              }}
            >
              Yes, Delete
            </AlertDialogAction>
            <AlertDialogCancel
              className="w-full py-4 rounded-xl font-semibold text-[16px] text-[#8B2121] dark:text-red-400 border-2 border-[#8B2121]/10 dark:border-white/10 bg-transparent"
            >
              No, Keep it
            </AlertDialogCancel>
          </div>
          <p className="text-center text-xs text-gray-400 dark:text-gray-500 pb-4">
            This action cannot be undone.
          </p>
        </AlertDialogContent>
      </AlertDialog>

      <RecordSuccessPopup
        isOpen={showSharePopup}
        onClose={() => setShowSharePopup(false)}
        onShare={() => {
          if (lastInvoiceData) {
            const doc = generateInvoice(
              lastInvoiceData.items,
              lastInvoiceData.total,
              lastInvoiceData.discount,
            );
            doc.save(`Invoice_${Date.now()}.pdf`);
            shareOnWhatsApp(
              lastInvoiceData.total,
              lastInvoiceData.items,
              lastInvoiceData.discount,
            );
            setShowSharePopup(false);
          }
        }}
        total={lastInvoiceData?.total || 0}
        discount={lastInvoiceData?.discount || 0}
        items={lastInvoiceData?.items || []}
        summary={lastInvoiceData?.summary || ""}
        orderId={lastInvoiceData?.orderId}
        fullOrderId={lastInvoiceData?.fullOrderId}
        isGstBill={lastInvoiceData?.isGstBill}
      />
    </div>
  );
}